/**
 * Deadline countdowns.
 *
 * The server is the only authority on how long is left: every poll carries
 * `secondsRemaining`, and the client merely interpolates between polls. We
 * measure elapsed time with `performance.now()`, which is monotonic — a trader
 * whose laptop clock drifts, sleeps, or crosses a DST boundary still sees the
 * server's answer rather than their own machine's opinion.
 *
 * One interval drives every countdown on the page, so a screen with fifty
 * pending payments still ticks once a second, not fifty times.
 */

import { useSyncExternalStore } from 'react';

let tick = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

const TICK_MS = 1000;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (intervalId === null) {
    intervalId = setInterval(() => {
      tick += 1;
      for (const l of listeners) l();
    }, TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

function getSnapshot(): number {
  return tick;
}

/** Subscribe a component to the shared one-second tick. */
function useTick(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export const URGENCY = {
  /** Below this many seconds the countdown turns red. */
  criticalSeconds: 5 * 60,
  /** Below this many seconds it turns amber. */
  warningSeconds: 10 * 60,
} as const;

export type Urgency = 'calm' | 'warning' | 'critical' | 'expired';

function urgencyOf(secondsLeft: number): Urgency {
  if (secondsLeft <= 0) return 'expired';
  if (secondsLeft <= URGENCY.criticalSeconds) return 'critical';
  if (secondsLeft <= URGENCY.warningSeconds) return 'warning';
  return 'calm';
}

/**
 * Convert a wall-clock timestamp (e.g. TanStack Query's `dataUpdatedAt`) into a
 * reading on the monotonic `performance.now()` timeline.
 */
export function toPerfTime(wallClockMs: number): number {
  return performance.now() - (Date.now() - wallClockMs);
}

export interface CountdownState {
  secondsLeft: number;
  urgency: Urgency;
  isExpired: boolean;
}

/**
 * @param serverSeconds `secondsRemaining` as of the last successful fetch.
 * @param syncedAtMs    `performance.now()` reading taken when that fetch landed.
 */
export function useCountdown(serverSeconds: number, syncedAtMs: number): CountdownState {
  useTick();

  const elapsedSeconds = (performance.now() - syncedAtMs) / 1000;
  const secondsLeft = Math.max(0, Math.round(serverSeconds - elapsedSeconds));

  return {
    secondsLeft,
    urgency: urgencyOf(secondsLeft),
    isExpired: secondsLeft <= 0,
  };
}
